import { useState } from "react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  Users,
  Globe,
  Calculator,
  RefreshCw,
  DollarSign,
  Clock,
} from "lucide-react";
import { toast } from "sonner";
import { MarketsTab } from "@/components/admin/Payroll/MarketsTab";
import { FTERulesTab } from "@/components/admin/Payroll/FTERulesTab";
import { SalariesTab } from "@/components/admin/Payroll/SalariesTab";
import { PositionsTab } from "@/components/admin/Payroll/PositionsTab";
import { useAdminPayroll } from "@/hooks/useAdminPayroll";

const initialDepartments = [
  {
    id: "management",
    name: "Management",
    color: "#3b82f6",
    icon: "Crown",
    isActive: true,
  },
  {
    id: "front-office",
    name: "Front Office",
    color: "#60a5fa",
    icon: "ConciergeBell",
    isActive: true,
  },
  {
    id: "housekeeping",
    name: "Housekeeping",
    color: "#10b981",
    icon: "Sparkles",
    isActive: true,
  },
  {
    id: "fnb",
    name: "Food & Beverage",
    color: "#f59e0b",
    icon: "UtensilsCrossed",
    isActive: true,
  },
  {
    id: "spa",
    name: "Spa & Wellness",
    color: "#8b5cf6",
    icon: "Leaf",
    isActive: true,
  },
  {
    id: "maintenance",
    name: "Maintenance",
    color: "#06b6d4",
    icon: "Wrench",
    isActive: true,
  },
  {
    id: "admin",
    name: "Sales & Marketing",
    color: "#6b7280",
    icon: "FileText",
    isActive: true,
  },
];

const formatMonthDay = (date: Date): string => {
  const monthNames = [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec",
  ];
  const month = monthNames[date.getMonth()];
  const day = date.getDate();
  return `${month} ${day}`;
};

const formatTime = (date: Date): string => {
  let hours = date.getHours();
  const minutes = date.getMinutes().toString().padStart(2, "0");
  const ampm = hours >= 12 ? "PM" : "AM";

  hours = hours % 12;
  hours = hours || 12;

  return `${hours}:${minutes} ${ampm}`;
};

export default function AdminPayroll() {
  const [activeTab, setActiveTab] = useState("positions");

  const {
    departments,
    positions,
    markets,
    salaries,
    fteRules,
    loading,
    addDepartment,
    updateDepartment,
    deleteDepartment,
    addPosition,
    updatePosition,
    deletePosition,
    addMarket,
    updateMarket,
    deleteMarket,
    upsertSalary,
    getSalaryEntry,
    addFTERule,
    updateFTERule,
    deleteFTERule,
    getPositionsByDepartment,
    getStats,
    refreshData,
  } = useAdminPayroll();

  const populateInitialDepartments = async () => {
    try {
      if (departments.length === 0) {
        const promises = initialDepartments.map((dept) =>
          addDepartment({
            name: dept.name,
            color: dept.color,
            display_order: dept.displayOrder,
            is_active: dept.isActive,
          }),
        );

        await Promise.all(promises);
        toast.success("Initial departments populated");
      } else {
        toast.info("Departments already exist");
      }
    } catch (error) {
      console.error("Error populating departments:", error);
      toast.error("Failed to populate departments");
    }
  };

  const stats = getStats();
  const positionsByDepartment = getPositionsByDepartment();

  const lastUpdated = new Date(stats.lastUpdated);
  const formattedDate = formatMonthDay(lastUpdated);
  const formattedTime = formatTime(lastUpdated);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-blue mx-auto"></div>
          <p className="mt-4 text-muted-foreground">
            Loading payroll configuration...
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">
            Payroll Configuration
          </h1>
          <p className="text-muted-foreground mt-1">
            Manage positions, markets, salaries, and FTE rules
          </p>
        </div>
        <div className="flex gap-2">
          {departments.length === 0 && (
            <Button
              variant="outline"
              onClick={populateInitialDepartments}
              className="bg-green-50 text-green-700 hover:bg-green-100 border-green-200"
            >
              Load Demo Departments
            </Button>
          )}
          <Button variant="outline" onClick={refreshData}>
            <RefreshCw className="h-4 w-4 mr-2" />
            Refresh Data
          </Button>
        </div>
      </div>

      <div className="grid gap-4 md:grid-cols-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium">
              Active Positions
            </CardTitle>
            <Users className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.activePositions}</div>
            <p className="text-xs text-muted-foreground">
              across {stats.totalDepartments} departments
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium">
              Markets Configured
            </CardTitle>
            <Globe className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.totalMarkets}</div>
            <p className="text-xs text-muted-foreground">with salary data</p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium">FTE Rules</CardTitle>
            <Calculator className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.totalFTERules}</div>
            <p className="text-xs text-muted-foreground">calculation rules</p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium">Last Updated</CardTitle>
            <Clock className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{formattedDate}</div>
            <p className="text-xs text-muted-foreground">{formattedTime}</p>
          </CardContent>
        </Card>
      </div>

      <Tabs
        value={activeTab}
        onValueChange={setActiveTab}
        className="space-y-4"
      >
        <TabsList className="grid w-full grid-cols-4">
          <TabsTrigger value="positions" className="flex items-center gap-2">
            <Users className="h-4 w-4" />
            Positions
          </TabsTrigger>
          <TabsTrigger value="salaries" className="flex items-center gap-2">
            <DollarSign className="h-4 w-4" />
            Market Salaries
          </TabsTrigger>
          <TabsTrigger value="fte-rules" className="flex items-center gap-2">
            <Calculator className="h-4 w-4" />
            FTE Rules
          </TabsTrigger>
          <TabsTrigger value="markets" className="flex items-center gap-2">
            <Globe className="h-4 w-4" />
            Labor Markets
          </TabsTrigger>
        </TabsList>

        <TabsContent value="positions" className="space-y-4">
          <PositionsTab
            departments={departments.filter((d) => d.is_active)}
            positionsByDepartment={positionsByDepartment}
            onAddPosition={addPosition}
            onUpdatePosition={updatePosition}
            onDeletePosition={deletePosition}
            onAddDepartment={addDepartment}
            onUpdateDepartment={updateDepartment}
            onDeleteDepartment={deleteDepartment}
          />
        </TabsContent>

        <TabsContent value="salaries" className="space-y-4">
          <SalariesTab
            positions={positions.filter((p) => p.is_active)}
            markets={markets.filter((m) => m.is_active)}
            salaries={salaries}
            onAddMarket={addMarket}
            onUpdateMarket={updateMarket}
            onDeleteMarket={deleteMarket}
            onUpsertSalary={upsertSalary}
            getSalaryEntry={getSalaryEntry}
          />
        </TabsContent>

        <TabsContent value="fte-rules" className="space-y-4">
          <FTERulesTab
            positions={positions.filter((p) => p.is_active)}
            fteRules={fteRules.filter((r) => r.is_active)}
            onAddRule={addFTERule}
            onUpdateRule={updateFTERule}
            onDeleteRule={deleteFTERule}
          />
        </TabsContent>

        <TabsContent value="markets" className="space-y-4">
          <MarketsTab
            markets={markets.filter((m) => m.is_active)}
            onAddMarket={addMarket}
            onUpdateMarket={updateMarket}
            onDeleteMarket={deleteMarket}
          />
        </TabsContent>
      </Tabs>
    </div>
  );
}
